import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import EditIcon from "assets/icons/Edit";
import GenericAvatar from "assets/icons/GenericAvatar";
import { useToast } from "contexts/ToastContext";
import {
  validateFile,
  resizeAndCropImage,
  getImagePreviewUrl,
} from "helpers/imageProcessing";
import { CloudUploadIcon } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { Controller, useFormContext, FieldPath } from "react-hook-form";

interface GenericImageUploadProps<T extends Record<string, unknown>> {
  readonly name: FieldPath<T>;
  readonly label?: string;
  readonly showButton?: boolean;
  readonly defaultImage?: string;
}

function GenericImageUpload<T extends Record<string, unknown>>({
  name,
  label = "Upload Image",
  showButton = true,
  defaultImage,
}: Readonly<GenericImageUploadProps<T>>) {
  const { control } = useFormContext<T>();
  const { showError } = useToast();
  const [currentPreviewUrl, setCurrentPreviewUrl] = useState<string | null>(
    defaultImage || null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempPreviewUrl, setTempPreviewUrl] = useState<string | null>(null);
  const [tempFile, setTempFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update preview URL when defaultImage prop changes (e.g., after API refetch)
  useEffect(() => {
    if (defaultImage) {
      setCurrentPreviewUrl(defaultImage);
    }
  }, [defaultImage]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setTempPreviewUrl(null);
    setTempFile(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTempPreviewUrl(null);
    setTempFile(null);
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      showError("Invalid File", validationError);
      setIsProcessing(false);
      return;
    }

    try {
      // Process and resize image
      const processedFile = await resizeAndCropImage(file);
      const previewUrl = await getImagePreviewUrl(processedFile);

      setTempFile(processedFile);
      setTempPreviewUrl(previewUrl);
    } catch {
      showError(
        "Processing Failed",
        "Failed to process image. Please try again.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdate = (onChange: (file: File | null) => void) => {
    if (tempFile && tempPreviewUrl) {
      onChange(tempFile);
      setCurrentPreviewUrl(tempPreviewUrl);
      handleCloseModal();
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => {
        const displayImage =
          currentPreviewUrl || (typeof value === "string" ? value : null);

        return (
          <>
            <Box className="flex items-center gap-4">
              <Box
                onClick={handleOpenModal}
                className="cursor-pointer relative"
              >
                {displayImage ? (
                  <img
                    src={displayImage}
                    alt="Profile"
                    width={100}
                    height={100}
                    className="object-cover rounded-full"
                  />
                ) : (
                  <GenericAvatar size={100} />
                )}

                {/* Edit icon */}
                <Box
                  className="absolute bottom-1 right-0 rounded-full border transition-colors"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                  }}
                >
                  <EditIcon />
                </Box>
              </Box>

              {showButton && (
                <Button
                  variant="outlined"
                  onClick={handleOpenModal}
                  className="px-6 py-2 rounded-full"
                >
                  {label}
                </Button>
              )}
            </Box>

            {/* Upload Modal */}
            <Dialog
              open={isModalOpen}
              onClose={handleCloseModal}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Update Picture</DialogTitle>

              <DialogContent>
                <Box
                  className="!flex !flex-col !items-center !space-y-6 !py-4"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "6",
                    py: "4",
                  }}
                >
                  {/* Current/Preview Image */}
                  <Box className="size-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-gray-200">
                    {tempPreviewUrl || displayImage ? (
                      <img
                        src={tempPreviewUrl || displayImage || ""}
                        alt="Profile preview"
                        width={320}
                        height={320}
                      />
                    ) : (
                      <GenericAvatar size={100} />
                    )}
                  </Box>

                  {/* Upload Button */}
                  <Button
                    variant="outlined"
                    onClick={handleFileSelect}
                    disabled={isProcessing}
                    className="px-6 py-2"
                  >
                    <CloudUploadIcon className="mr-2" />
                    {isProcessing ? "Processing..." : "Upload Image"}
                  </Button>
                </Box>

                {/* Requirements Text */}
                <Typography
                  variant="body2"
                  className="text-center text-gray-600"
                >
                  Image dimension should be 320x320 pixels. Maximum file size is
                  5MB.
                  <br />
                  The system will automatically resize and crop the image from
                  the center.
                </Typography>

                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </DialogContent>

              <DialogActions className="px-6 flex justify-center pb-4">
                <Button onClick={handleCloseModal} variant="outlined">
                  Cancel
                </Button>
                {tempPreviewUrl && (
                  <Button
                    onClick={() => handleUpdate(onChange)}
                    variant="contained"
                    disabled={isProcessing}
                  >
                    Update
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          </>
        );
      }}
    />
  );
}

export default GenericImageUpload;
